<template>
  <div class="mt-[60px] mx-auto max-w-[472px] flex flex-col items-center">
    <div class="login-title">Type in your details</div>
    <div class="login-please-type">
      We care about your privacy, please input the below information to securely
      access the questionnaire
    </div>
    <div class="w-full mx-auto max-w-[434px]" v-if="!presetAccessionId">
      <div class="regular-label mb-1 text-base">Accession ID</div>
      <input
        id="first-name"
        class="inputbox-layout grey-inputbox"
        v-model.trim="accession_id"
        placeholder=""
      />
    </div>
    <div class="w-full mx-auto max-w-[434px]">
      <div class="regular-label mb-1 text-base">First name</div>
      <input
        id="first-name"
        class="inputbox-layout grey-inputbox"
        v-model.trim="fname"
        placeholder=""
      />
    </div>
    <div class="w-full mx-auto max-w-[434px]">
      <div class="regular-label mb-1 text-base">Last name</div>
      <input
        id="last-name"
        class="inputbox-layout grey-inputbox"
        v-model.trim="lname"
        placeholder=""
      />
    </div>
    <div class="w-full mx-auto max-w-[434px]">
      <div class="mb-1 text-base regular-label">Date of Birth</div>
      <input
        id="dob"
        class="inputbox-layout grey-inputbox"
        type="text"
        v-model="date"
        placeholder="MM/DD/YYYY"
        @input="formattedDate"
      />
    </div>
    <div></div>
    <el-button
      id="submit"
      class="loading-button mt-12"
      @click="submitForm"
      :disabled="!completeForm"
      :loading="loadingQnr"
      tabindex="4"
    >
      Submit
    </el-button>
  </div>
</template>

<script>
import { useWindowSize } from "@vueuse/core";
import { ElNotification } from "element-plus";
import { mapActions } from "pinia";
import { useCommonStore } from "@/stores/CommonStore.ts";
import gql from "graphql-tag";
import { pns_graphal_client } from "@/utils/graphQLRequests.js";
import { isValidAccessionId, normalizeAccessionId } from "@/utils/accessionId.js";

// VP-18267: the REST /patient/guest_login route on coresamples was retired in 2026-03 and has
// answered 401 for every caller since. The same gRPC PatientGuestLogIn is served through this
// trans-service mutation, which pns-portal already uses. The frontend branches on the response
// code; the server collapses failure text to one generic message on purpose (VP-17261).
const PATIENT_GUEST_LOGIN = gql`
  mutation patientGuestLogin($input: PatientGuestLoginRequest!) {
    patientGuestLogin(input: $input) {
      token
      code
      errorMessage
    }
  }
`;

export default {
  setup() {
    const { width } = useWindowSize();
    return {
      width,
    };
  },
  props: {
    questionnaireType: {
      type: String,
      default: "",
    },
    accessionId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loadingQnr: false,
      fname: "",
      lname: "",
      date: "",
      accession_id: "",
    };
  },
  mounted() {
    if (this.presetAccessionId) {
      this.accession_id = this.presetAccessionId;
      this.setAccessionId(this.accession_id);
    }
  },
  methods: {
    ...mapActions(()=>useCommonStore(), [
      "setAccessionId",
      "setSampleId",
      "setJwtToken",
    ]),
    formattedDate() {
      // Remove all non-numeric characters from the input
      let cleaned = this.date.replace(/[^0-9]/g, "");
      // Add slashes between the numbers
      if (cleaned.length > 2) {
        cleaned = cleaned.slice(0, 2) + "/" + cleaned.slice(2);
      }
      if (cleaned.length > 5) {
        cleaned = cleaned.slice(0, 5) + "/" + cleaned.slice(5);
      }
      if (cleaned.length > 10) {
        cleaned = cleaned.slice(0, 10);
      }
      // Return the formatted date
      this.date = cleaned;
    },
    showFailNotification(errorMessage) {
      let position = this.width < 480 ? "bottom-left" : "top-right";
      ElNotification({
        title: "Verify Failed",
        message: errorMessage,
        type: "error",
        position: position,
      });
    },
    verificationErrorMessage(code) {
      if (code === 403) {
        return "Cannot find the patient information. Please check your information and try again.";
      }
      if (code === 422) {
        return `We noticed that your date of birth has not been entered into our system. Please update it first or contact your provider directly to ensure your date of birth is added.`;
      }
      return "There are some errors during the verification process. Please try to verify again.";
    },
    async submitForm() {
      let dobs = this.date.split("/");
      let dob = [dobs[2], dobs[0], dobs[1]].join("-");
      const input = {
        patient_birthdate: dob,
        patient_first_name: this.fname,
        patient_accession_id: this.accession_id,
        patient_last_name: this.lname,
        is_post_login_call: false,
      };
      this.loadingQnr = true;
      try {
        const { data, errors } = await pns_graphal_client.mutate({
          mutation: PATIENT_GUEST_LOGIN,
          variables: { input },
          fetchPolicy: "no-cache",
        });
        if (errors && errors.length > 0) {
          throw { code: 500, errorMessage: errors[0].message };
        }
        const result = data && data.patientGuestLogin;
        if (!result || result.code !== 200 || !result.token) {
          throw result || { code: 500 };
        }
        this.setJwtToken(result.token);
        this.$router.push(`/${this.questionnaireType}/${this.accession_id}`);
      } catch (err) {
        console.log("guest login failed", err && err.code);
        this.showFailNotification(this.verificationErrorMessage(err && err.code));
      } finally {
        this.loadingQnr = false;
      }
    },
  },
  computed: {
    // VP-18267: a preset accession that is not numeric (e.g. the literal "undefined" from a
    // broken link) counts as absent, so the accession input shows and the patient can fix it.
    presetAccessionId() {
      return normalizeAccessionId(this.accessionId);
    },
    completeForm() {
      return this.fname && this.lname && this.date.length === 10 && isValidAccessionId(this.accession_id);
    },
  },
};
</script>
<style scoped>
.login-title {
  font-weight: 700;
  font-size: 24px;
  color: #4a4a4a;
  margin-bottom: 19px;
}
.login-please-type {
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  color: #4a4a4a;
  margin-bottom: 36px;
}
.regular-label {
  color: rgba(0, 0, 0, 0.87);
}
.red-label {
  color: #e14942;
}
.inputbox-layout {
  width: 100%;
  height: 48px;
  border-radius: 5px;
  margin-bottom: 26px;
  padding-left: 27px;
}

.grey-inputbox {
  border: 1px solid #979797;
}

.date-input:focus,
.grey-inputbox:focus {
  outline: none !important;
}

.day-input,
.month-input {
  width: 28px;
  text-align: center;
}

.divider_placeholder {
  color: rgba(131, 131, 131, 0.655);
}

.divider_black {
  color: black;
}
.red-inputbox {
  border: 1px solid #e14942;
}
.red-inputbox:focus {
  border: 1px solid #e14942;
  outline: none !important;
}

.el-button.loading-button {
  width: 163px;
  height: 42px;
  border-radius: 6px;
  font-size: 16px;
  color: #ffffff;
  background: #3498db;
}

.el-button.loading-button:disabled {
  background: #d8d8d8;
}

.el-button.loading-button.is-loading {
  background: #3498db;
}

.visiable-dob-reminder {
  margin-top: -18px;
  margin-bottom: 41px;
  color: #e14942;
  visibility: visible;
  font-size: 12px;
  font-weight: 400;
}
.el-notification.right {
  height: 149px;
  width: 519px;
  border-radius: 8px;
  padding: 36px 40px 36px 40px;
}
.el-notification__title {
  font-size: 21px;
  font-weight: 600;
  margin-bottom: 8px;
}
.el-notification__content p {
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #979797;
}
.el-notification__group {
  margin-left: 26.5px;
}

@media all and (max-width: 480px) {
  .login-title {
    font-size: 18px;
    margin-bottom: 12px;
  }
  .login-please-type {
    font-size: 12px;
    line-height: 14px;
    margin-bottom: 30px;
  }
  .regular-label {
    font-size: 12px;
    line-height: 12px;
    margin-bottom: 6px;
  }

  .red-label {
    font-size: 12px;
    line-height: 12px;
    margin-bottom: 6px;
  }
  .inputbox-layout {
    height: 36px;
    margin-bottom: 12px;
    padding: 11px 0px 11px 18px;
  }
  .el-button.loading-button {
    width: 140px;
    height: 36px;
    margin-top: 40px;
  }
  .visiable-dob-reminder {
    margin-top: -6px;
    font-size: 10px;
  }
  .el-notification.left {
    width: 300px;
    height: 87px;
    padding: 21px, 23px, 21px, 23px;
    position: absolute;
    bottom: 0px;
    margin-right: auto;
    margin-left: auto;
    left: 0px;
    right: 0px;
  }
  .el-notification__title {
    font-size: 12px;
    margin-bottom: 4.6px;
  }
  .el-notification__content p {
    font-size: 10px;
    line-height: 13px;
  }
  .el-notification__group {
    margin-left: 15px;
  }
}
</style>
